import React from 'react';
import { Box, Tab, Tabs as MuiTabs, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { TabsIcons } from '../assets';
import { useQuery } from 'react-query';
import { getFolder, getFolderI } from '../api/services';

interface TabPanelProps {
  icon: string;
  label: string;
  route: string;
}

interface TabsProps {
  selectedTab: string;
  onTabChange: (route: string) => void;
}

const Tabs: React.FC<TabsProps> = ({ selectedTab, onTabChange }) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const { data: Tabsdata, isLoading, isError } = useQuery('alltabs', getFolderI, {
    enabled: !id,
  });

  const { data: folderData } = useQuery(
    ['folderData', id],
    () => getFolder(id || ''),
    { enabled: !!id }
  );

  const adminTabs: TabPanelProps[] = [
    { icon: TabsIcons.DashboardIcon, label: 'All Files', route: '/admin' }, // Default tab
    ...(id
      ? folderData?.data?.subfolders?.map((subfolder: any) => ({
          icon: TabsIcons.DashboardIcon, // Default icon for all dynamic tabs
          label: subfolder.name, // Subfolder name
          route: `/folder/${subfolder._id}`, // Dynamic route
        })) || []
      : Tabsdata?.data?.map((folder: any) => ({
          icon: TabsIcons.DashboardIcon, // Default icon for all dynamic tabs
          label: folder.name, // Folder name
          route: `/folder/${folder._id}`, // Dynamic route
        })) || []),
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    navigate(newValue);
    onTabChange(newValue); 
  };

  return (
    <Box
      sx={{
        width: '200px',
        height: 'calc(100vh - 64px)',
        backgroundColor: theme => theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '10px',
        position: 'fixed',
        top: '64px',
        boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <MuiTabs
        orientation="vertical"
        value={selectedTab}
        onChange={handleChange}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {adminTabs.map((tab, index) => (
          <Tab
            key={index}
            value={tab.route}
            icon={
              <img
                src={tab.icon}
                alt={tab.label}
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '4px',
                  filter: selectedTab.startsWith(tab.route) ? 'invert(1)' : 'invert(0)',
                  transition: 'filter 0.25s',
                }}
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{
                  color: selectedTab.startsWith(tab.route) ? '#fff' : '#000',
                  marginLeft: '10px',
                }}
              >
                {tab.label}
              </Typography>
            }
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              backgroundColor: selectedTab.startsWith(tab.route)
                ? theme => theme.palette.primary.main
                : theme => theme.palette.secondary.main,
              opacity: selectedTab.startsWith(tab.route) ? 1 : 0.8,
              color: selectedTab.startsWith(tab.route) ? '#fff' : '#000',
              borderRadius: '4px',
              height: '48px',
              minHeight: '48px',
              padding: '10px 10px',
              mx: '15px',
              marginBottom: '8px',
              transition: 'background-color 0.2s, opacity 0.2s, color 0.2s',
              '&:hover': {
                backgroundColor: theme => theme.palette.primary.main,
                color: '#fff',
                opacity: 1,
              },
            }}
            iconPosition="start"
          />
        ))}
      </MuiTabs>
    </Box>
  );
};

export default Tabs;
